import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { endorsementCatalog, type EndorsementType } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';

import { NewEndorsementDialog } from '../../portfolio/NewEndorsementDialog';
import { FUNCIONALIDADE_PESSOA_FISICA } from '../../policyholders/rotulos';

/**
 * "Nova cotacao ou endosso" da lista de cotacoes da corretora: o dialogo do OnPoint
 * "Selecione o tipo de cotacao que deseja iniciar" (NCT-059 a NCT-064).
 *
 * "Nova cotacao Garantia" abre o assistente. "Nova cotacao Pessoa Fisica" abre a
 * cotacao do leiloeiro (P-NCT-3), desligada com o motivo quando a empresa nao tem o
 * tomador pessoa fisica. Os tipos de endosso habilitados da empresa levam ao endosso
 * que ja existe (P-END-1), pela escolha da apolice; sem tipo habilitado, nenhum cartao
 * de endosso aparece, como no OnPoint.
 */
export function QuoteTypeDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { can, hasFeature } = useAuth();
  const navigate = useNavigate();
  const pessoaFisica = hasFeature(FUNCIONALIDADE_PESSOA_FISICA);
  const [endosso, setEndosso] = useState<EndorsementType | null>(null);
  const podeEndossar = can('endossos.endosso.create');

  const tipos = useQuery({
    queryKey: ['endorsement-types', 'habilitados'],
    queryFn: ({ signal }) => endorsementCatalog.types(true, signal),
    enabled: open && podeEndossar,
    retry: false,
  });

  const cartao = cn(
    'flex flex-col items-start gap-0.5 rounded-md border border-line px-4 py-3 text-left',
    'hover:border-brand-500 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-brand-500',
    'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-line disabled:hover:bg-transparent',
  );

  return (
    <>
      <Modal
        open={open && endosso === null}
        onClose={onClose}
        title="Nova cotacao ou endosso"
        description="Selecione o tipo de cotacao que deseja iniciar"
        size="md"
        footer={
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className={cartao}
            onClick={() => {
              onClose();
              void navigate('/operacoes/nova/tomador');
            }}
          >
            <span className="text-sm font-medium text-ink-900">
              Nova cotacao Garantia
            </span>
            <span className="text-xs text-ink-600">
              Iniciar uma nova cotacao de Seguro Garantia.
            </span>
          </button>
          <button
            type="button"
            className={cartao}
            disabled={!pessoaFisica}
            title={
              pessoaFisica
                ? undefined
                : 'A empresa nao tem o cadastro de tomador pessoa fisica habilitado.'
            }
            onClick={() => {
              onClose();
              void navigate('/operacoes/nova-pf/tomador');
            }}
          >
            <span className="text-sm font-medium text-ink-900">
              Nova cotacao Pessoa Fisica
            </span>
            <span className="text-xs text-ink-600">
              Iniciar uma nova cotacao para Pessoa Fisica.
            </span>
          </button>
          {tipos.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar os tipos de endosso">
              {tipos.error.message}
            </Alert>
          ) : null}
          {(tipos.data ?? []).map((tipo) => (
            <button
              key={tipo.id}
              type="button"
              className={cartao}
              onClick={() => setEndosso(tipo)}
            >
              <span className="text-sm font-medium text-ink-900">{tipo.name}</span>
              <span className="text-xs text-ink-600">{tipo.description}</span>
            </button>
          ))}
        </div>
      </Modal>
      {endosso ? (
        <NewEndorsementDialog
          open
          tipoInicial={endosso}
          onClose={() => {
            setEndosso(null);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
